
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { ContentItem } from "@/components/dashboard/content-card/types";

export const useBoardContent = (boardId?: string) => {
  const { user } = useAuth();

  const { data: contentItems = [], isLoading, error, refetch } = useQuery({
    queryKey: ['boardContent', boardId],
    queryFn: async () => {
      if (!boardId || !user) return [];

      console.log(`[BOARD CONTENT] Fetching content for board: ${boardId}`);

      const { data, error } = await supabase
        .from('content_boards')
        .select(`
          content_id,
          position_x,
          position_y,
          z_index,
          content_items!inner (
            id,
            title,
            url,
            description,
            content_type,
            thumbnail_url,
            created_at,
            notes,
            metadata,
            archived,
            user_id
          )
        `)
        .eq('board_id', boardId)
        .eq('content_items.user_id', user.id)
        .eq('content_items.archived', false);

      if (error) {
        console.error('Error fetching board content:', error);
        throw error;
      }

      console.log(`[BOARD CONTENT] Found ${data?.length || 0} items for board ${boardId}`);

      // Transform the data to match ContentItem interface
      const transformedItems: ContentItem[] = (data || []).map((item: any) => ({
        id: item.content_items.id,
        title: item.content_items.title,
        url: item.content_items.url,
        description: item.content_items.description,
        content_type: item.content_items.content_type,
        thumbnail_url: item.content_items.thumbnail_url,
        created_at: item.content_items.created_at,
        notes: item.content_items.notes,
        metadata: item.content_items.metadata,
        archived: item.content_items.archived,
        user_id: item.content_items.user_id,
        // Board-specific properties
        board_position_x: item.position_x,
        board_position_y: item.position_y,
        board_z_index: item.z_index
      }));

      return transformedItems;
    },
    enabled: !!boardId && !!user,
    staleTime: 30000, // Cache for 30 seconds
  });

  return {
    contentItems,
    isLoading,
    error,
    refetch
  };
};
